import _capitalize from 'lodash/capitalize';
import _invert from 'lodash/invert';

type Invert<T extends Record<PropertyKey, PropertyKey>> = {
  [P in keyof T as T[P]]: P;
};

/**
 * === IMPORTANT ===
 *
 * When adding a new enum value, make sure to add a corresponding
 * ID mapping in the `dateRangeToIds` object below.
 */
export enum DateRange {
  NEXT_14_DAYS = 'Next 14 days',
  NEXT_30_DAYS = 'Next 30 days',
  NEXT_60_DAYS = 'Next 60 days',
  LAST_MONTH = 'Last month',
  THIS_MONTH = 'This month',
  NEXT_MONTH = 'Next month',
  THIS_QUARTER = 'This quarter',
  NEXT_QUARTER = 'Next quarter',
  NEXT_12_MONTHS = 'Next 12 months',
  UNTIL_NEXT_PAYCHECK = 'Until next paycheck',
}

/**
 * === NOTE: FOR BACKWARD COMPATIBILITY ===
 *
 * Although this object's values look like labels they are IDs
 * that have been made to be backwards compatible with old clients.
 *
 * They are intentionally all sentence case since they are normalized
 * before lookups are preformed.
 *
 * Any new IDs should also be sentence case
 */
export const dateRangeToIds = {
  [DateRange.NEXT_14_DAYS]: 'Next 14 days',
  [DateRange.NEXT_30_DAYS]: 'Next 30 days',
  [DateRange.NEXT_60_DAYS]: 'Next 60 days',
  [DateRange.LAST_MONTH]: 'Last month',
  [DateRange.THIS_MONTH]: 'This month',
  [DateRange.NEXT_MONTH]: 'Next month',
  [DateRange.THIS_QUARTER]: 'This quarter',
  [DateRange.NEXT_QUARTER]: 'Next quarter',
  [DateRange.NEXT_12_MONTHS]: 'Next 12 months',
  [DateRange.UNTIL_NEXT_PAYCHECK]: 'Until next paycheck',
} as const;

const idsToDateRange = _invert(dateRangeToIds) as Invert<typeof dateRangeToIds>;

function isValidDateRangeId(key?: string): key is keyof typeof idsToDateRange {
  return typeof key === 'string' && key in idsToDateRange;
}

export function getDateRangeLabelFromId(id: string | undefined) {
  if (!id) return null;

  const normalizedId = _capitalize(id);

  if (isValidDateRangeId(normalizedId)) {
    return idsToDateRange[normalizedId];
  }

  return null;
}

/**
 * This helper function ensures a date range label exists within a range of labels.
 *
 * Useful if feature flags are used to build the list of available options.
 *
 * @param id prefs value holding the DateRange ID
 * @param range array of allowed DateRange labels
 * @param fallback a fallback DateRange label
 * @returns
 */
export const getDateRangeLabelFromIdWithinRange = (
  id: string | undefined,
  range: DateRange[],
  fallback: DateRange = DateRange.NEXT_30_DAYS,
) => {
  const label = getDateRangeLabelFromId(id);

  if (label && range.includes(label)) {
    return label;
  }

  return fallback;
};

export function getDateRangeIdFromLabel(label: DateRange) {
  return dateRangeToIds[label];
}
